import React, { createContext, useContext, useState, useEffect } from 'react';
import { Sport } from '../types';

interface SportContextType {
  selectedSport: Sport;
  setSelectedSport: (sport: Sport) => void;
}

const SportContext = createContext<SportContextType | undefined>(undefined);

const SPORT_STORAGE_KEY = 'rtpicker_selected_sport';

export const SportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedSport, setSelectedSportState] = useState<Sport>(() => {
    // Initialize from localStorage or default to 'football'
    const stored = localStorage.getItem(SPORT_STORAGE_KEY);
    return (stored === 'football' || stored === 'basketball') ? stored : 'football';
  });

  const setSelectedSport = (sport: Sport) => {
    setSelectedSportState(sport);
    localStorage.setItem(SPORT_STORAGE_KEY, sport);
  };

  useEffect(() => {
    // Sync with localStorage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SPORT_STORAGE_KEY && (e.newValue === 'football' || e.newValue === 'basketball')) {
        setSelectedSportState(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <SportContext.Provider value={{ selectedSport, setSelectedSport }}>
      {children}
    </SportContext.Provider>
  );
};

export const useSport = () => {
  const context = useContext(SportContext);
  if (context === undefined) {
    throw new Error('useSport must be used within a SportProvider');
  }
  return context;
};
